import { useState, type FormEvent } from "react";
import { APP_ROLES, type AppRole } from "@shop-ledger/shared-types";
import { Button, DataTable, Input, type DataTableColumn } from "@shop-ledger/ui-components";
import { useAuth } from "../../auth/AuthContext";
import { createUser, deleteUser, listUsers, updateUser } from "../../api/usersApi";
import type { UserInviteOut, UserManagementOut } from "../../api/types";
import { BranchSelect } from "../../components/BranchSelect";
import { ApiError } from "../../lib/apiClient";
import { StatusBanner } from "../../components/StatusBanner";
import { useAsyncData } from "../../lib/useAsyncData";

/**
 * Staff management — `GET/POST /users`, `PATCH /users/{id}`, `DELETE /users/{id}`
 * (§10.1). The business owner manages their own staff end to end: add a new hire (role
 * + a real branch dropdown, not a text field), edit their role/branch, deactivate/
 * reactivate, or remove them outright. `GET/POST/DELETE /users` are `OWNER`-only;
 * `GET /users` also allows `BRANCH_MANAGER` (scoped server-side to their own branch) —
 * this page renders whatever comes back and only shows write controls when
 * `canManageStaff` (OWNER) is true.
 */
export function StaffPage() {
  const { user, ensureFreshToken } = useAuth();
  const canManage = user?.role === "OWNER";
  const usersState = useAsyncData(() => ensureFreshToken().then((token) => listUsers(token)), []);

  const [email, setEmail] = useState("");
  const [role, setRole] = useState<AppRole>("TELLER");
  const [branchId, setBranchId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [lastInvite, setLastInvite] = useState<UserInviteOut | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editRole, setEditRole] = useState<AppRole>("TELLER");
  const [editBranchId, setEditBranchId] = useState("");
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const [deletingTarget, setDeletingTarget] = useState<UserManagementOut | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  async function handleAddStaff(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    setLastInvite(null);
    setSubmitting(true);
    try {
      const token = await ensureFreshToken();
      const invite = await createUser(token, { email, role, assigned_branch_id: branchId || undefined });
      setLastInvite(invite);
      setEmail("");
      usersState.reload();
    } catch (error) {
      setFormError(error instanceof ApiError ? error.message : "Could not add staff member.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleConfirmDelete() {
    if (!deletingTarget) return;
    setDeleteError(null);
    setDeleteSubmitting(true);
    try {
      const token = await ensureFreshToken();
      await deleteUser(token, deletingTarget.id);
      setDeletingTarget(null);
      usersState.reload();
    } catch (error) {
      setDeleteError(error instanceof ApiError ? error.message : "Could not remove staff member.");
    } finally {
      setDeleteSubmitting(false);
    }
  }

  function startEdit(target: UserManagementOut) {
    setEditingId(target.id);
    setEditRole(target.role as AppRole);
    setEditBranchId(target.assigned_branch_id ?? "");
    setEditError(null);
  }

  async function handleSaveEdit(event: FormEvent) {
    event.preventDefault();
    if (!editingId) return;
    setEditError(null);
    setEditSubmitting(true);
    try {
      const token = await ensureFreshToken();
      await updateUser(token, editingId, {
        role: editRole,
        assigned_branch_id: editBranchId || undefined,
        clear_assigned_branch: editBranchId === "",
      });
      setEditingId(null);
      usersState.reload();
    } catch (error) {
      setEditError(error instanceof ApiError ? error.message : "Could not save changes.");
    } finally {
      setEditSubmitting(false);
    }
  }

  async function toggleActive(target: UserManagementOut) {
    try {
      const token = await ensureFreshToken();
      await updateUser(token, target.id, { is_active: !target.is_active });
      usersState.reload();
    } catch (error) {
      setEditError(error instanceof ApiError ? error.message : "Could not update status.");
    }
  }

  const columns: DataTableColumn<UserManagementOut, keyof UserManagementOut>[] = [
    { key: "email", header: "Email", render: (r) => r.email },
    { key: "role", header: "Role", render: (r) => r.role },
    { key: "assigned_branch_id", header: "Branch", render: (r) => (r.assigned_branch_id ? <span className="muted">{r.assigned_branch_id.slice(0, 8)}</span> : <span className="muted">— (unassigned)</span>) },
    {
      key: "is_active",
      header: "Status",
      render: (r) => <span className={`badge ${r.is_active ? "badge--current" : "badge--overdue"}`}>{r.is_active ? "Active" : "Deactivated"}</span>,
    },
    canManage
      ? {
          key: "id",
          header: "",
          render: (r) => (
            <div style={{ display: "flex", gap: 8 }}>
              <Button variant="ghost" size="md" type="button" onClick={() => startEdit(r)}>
                Edit
              </Button>
              <Button
                variant={r.is_active ? "danger" : "secondary"}
                size="md"
                type="button"
                disabled={r.is_active && r.id === user?.id}
                title={r.is_active && r.id === user?.id ? "You cannot deactivate your own account" : undefined}
                onClick={() => void toggleActive(r)}
              >
                {r.is_active ? "Deactivate" : "Reactivate"}
              </Button>
              <Button
                variant="danger"
                size="md"
                type="button"
                disabled={r.id === user?.id}
                title={r.id === user?.id ? "You cannot remove your own account" : undefined}
                onClick={() => {
                  setDeleteError(null);
                  setDeletingTarget(r);
                }}
              >
                Remove
              </Button>
            </div>
          ),
        }
      : { key: "id", header: "", render: () => null },
  ];

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Staff</h1>
          <p className="page__subtitle">Add staff, assign them to a branch, and manage roles/access.</p>
        </div>
      </div>

      {usersState.error ? <StatusBanner kind="error">{usersState.error}</StatusBanner> : null}
      {!canManage ? (
        <StatusBanner kind="info">
          Your role ({user?.role}) can view staff assigned to your branch. Adding/editing/deactivating/removing
          staff is OWNER-only.
        </StatusBanner>
      ) : null}

      {canManage ? (
        <div className="panel">
          <h2 className="panel__title">Add staff</h2>
          <form onSubmit={handleAddStaff}>
            <div className="form-grid">
              <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
              <div className="sl-field">
                <label className="sl-field__label" htmlFor="staff-role">
                  Role
                </label>
                <select id="staff-role" className="sl-input" value={role} onChange={(e) => setRole(e.target.value as AppRole)}>
                  {APP_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>
              <BranchSelect id="staff-branch" label="Branch" value={branchId} onChange={setBranchId} allOptionLabel="No branch (assign later)" />
            </div>
            {formError ? <StatusBanner kind="error">{formError}</StatusBanner> : null}
            <div className="form-actions">
              <Button type="submit" disabled={submitting}>
                {submitting ? "Adding…" : "Add staff member"}
              </Button>
            </div>
          </form>

          {lastInvite ? (
            <div className="banner banner--info" style={{ marginTop: 16 }}>
              <p style={{ marginTop: 0 }}>
                <strong>Account created for {lastInvite.user.email}.</strong> {lastInvite.note}
              </p>
              <p style={{ marginBottom: 4 }}>
                Setup link (hand this to them directly — Slack, WhatsApp, in person — it is shown once and cannot be
                retrieved again):
              </p>
              <code style={{ display: "block", wordBreak: "break-all", background: "#0f172a", color: "#e2e8f0", padding: "8px 10px", borderRadius: 6 }}>
                {`${window.location.origin}/accept-invite?token=${lastInvite.setup_token}`}
              </code>
              <p className="muted" style={{ marginBottom: 0 }}>
                Expires {new Date(lastInvite.setup_token_expires_at).toLocaleString()}.
              </p>
            </div>
          ) : null}
        </div>
      ) : null}

      {deletingTarget ? (
        <div className="panel">
          <h2 className="panel__title">Remove {deletingTarget.email}?</h2>
          <p className="muted">
            This removes them from the active staff list and blocks their login immediately. Their past sales and
            stock activity stay attributed to them — this cannot be undone from the UI.
          </p>
          {deleteError ? <StatusBanner kind="error">{deleteError}</StatusBanner> : null}
          <div className="form-actions">
            <Button variant="danger" type="button" disabled={deleteSubmitting} onClick={() => void handleConfirmDelete()}>
              {deleteSubmitting ? "Removing…" : "Remove staff member"}
            </Button>
            <Button variant="ghost" type="button" onClick={() => setDeletingTarget(null)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : null}

      {editingId ? (
        <div className="panel">
          <h2 className="panel__title">Edit staff member</h2>
          <form onSubmit={handleSaveEdit}>
            <div className="form-grid">
              <div className="sl-field">
                <label className="sl-field__label" htmlFor="edit-role">
                  Role
                </label>
                <select id="edit-role" className="sl-input" value={editRole} onChange={(e) => setEditRole(e.target.value as AppRole)}>
                  {APP_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>
              <BranchSelect id="edit-branch" label="Branch" value={editBranchId} onChange={setEditBranchId} allOptionLabel="No branch" />
            </div>
            {editError ? <StatusBanner kind="error">{editError}</StatusBanner> : null}
            <div className="form-actions">
              <Button type="submit" disabled={editSubmitting}>
                {editSubmitting ? "Saving…" : "Save"}
              </Button>
              <Button type="button" variant="ghost" onClick={() => setEditingId(null)}>
                Cancel
              </Button>
            </div>
          </form>
        </div>
      ) : null}

      <div className="panel">
        {usersState.loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <DataTable columns={columns} rows={usersState.data ?? []} getRowId={(row) => row.id} emptyMessage="No staff yet." caption="Staff" />
        )}
      </div>
    </div>
  );
}
